import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useAuth, type LocationData } from "@/contexts/AuthContext";
import {
  COLLAPSED_WIDTH,
  EXPANDED_WIDTH,
  HOVER_DELAY,
  MANY_LOCATIONS_THRESHOLD,
  RECENT_COUNT,
  RECENT_STORAGE_KEY,
  SNAP_UNIT,
} from "@/utils/constants";
import type { LocationSelectorProps } from "@/utils/types";
import styles from "./LocationSelector.module.css";

/**
 * Get initials for a location name.
 * Single word → first two letters (e.g. "Market" → "MA")
 * Multiple words → first letter of each word (e.g. "Deli Downtown" → "DD")
 */
function getInitials(name: string): string {
  const words = name.trim().split(/\s+/);
  if (words.length === 1) {
    return words[0].substring(0, 2).toUpperCase();
  }
  return words
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .substring(0, 2);
}

function loadRecentIds(): string[] {
  try {
    const raw = localStorage.getItem(RECENT_STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveRecentIds(ids: string[]): void {
  localStorage.setItem(RECENT_STORAGE_KEY, JSON.stringify(ids));
}

/**
 * Organization icon (building)
 */
const OrgIcon: React.FC = () => (
  <svg width="24" height="24" viewBox="0 0 28 28" fill="none">
    <rect
      x="7"
      y="6"
      width="7"
      height="3"
      rx="1"
      stroke="currentColor"
      strokeWidth="1.5"
    />
    <rect
      x="7"
      y="14"
      width="3"
      height="8"
      rx="1"
      stroke="currentColor"
      strokeWidth="1.5"
    />
    <rect
      x="21"
      y="10.5"
      width="3"
      height="11.5"
      rx="1"
      stroke="currentColor"
      strokeWidth="1.5"
    />
    <line
      x1="14"
      y1="7"
      x2="14"
      y2="9"
      stroke="currentColor"
      strokeWidth="1.5"
    />
    <line
      x1="14"
      y1="11.5"
      x2="14"
      y2="13.5"
      stroke="currentColor"
      strokeWidth="1.5"
    />
    <line
      x1="14"
      y1="16"
      x2="14"
      y2="18"
      stroke="currentColor"
      strokeWidth="1.5"
    />
    <line
      x1="14"
      y1="20.5"
      x2="14"
      y2="22.5"
      stroke="currentColor"
      strokeWidth="1.5"
    />
  </svg>
);

/**
 * Search icon
 */
const SearchIcon: React.FC = () => (
  <svg width="20" height="20" viewBox="0 0 28 28" fill="none">
    <circle cx="13" cy="13" r="7" stroke="currentColor" strokeWidth="1.5" />
    <path
      d="M18 18L22 22"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

export const LocationSelector: React.FC<LocationSelectorProps> = ({ isMobileMenuOpen = false }) => {
  const { locations, selectedLocation, selectLocation } = useAuth();
  const navigate = useNavigate();

  const [isExpanded, setIsExpanded] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [recentIds, setRecentIds] = useState<string[]>(loadRecentIds);

  const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const sidebarRef = useRef<HTMLDivElement>(null);

  const hasManyLocations = locations.length > MANY_LOCATIONS_THRESHOLD;

  // Build recent locations list (only when > 6 locations)
  const recentLocations = useMemo(() => {
    if (!hasManyLocations) return [];
    return recentIds
      .map((id) => locations.find((l) => l.id === id))
      .filter(Boolean) as LocationData[];
  }, [recentIds, locations, hasManyLocations]);

  // Filtered locations for search
  const filteredLocations = useMemo(() => {
    if (!searchQuery.trim()) return locations;
    const q = searchQuery.toLowerCase();
    return locations.filter((l) => l.name.toLowerCase().includes(q));
  }, [locations, searchQuery]);

  // Handle hover enter with delay
  const handleMouseEnter = useCallback(() => {
    hoverTimerRef.current = setTimeout(() => {
      setIsExpanded(true);
    }, HOVER_DELAY);
  }, []);

  // Handle hover leave — immediate collapse
  const handleMouseLeave = useCallback(() => {
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
    setIsExpanded(false);
  }, []);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    };
  }, []);

  // Force expanded on tablet when mobile menu is open
  useEffect(() => {
    const checkTabletState = () => {
      const isTablet = window.innerWidth >= 768 && window.innerWidth <= 1024;
      if (isTablet && isMobileMenuOpen) {
        setIsExpanded(true);
      } else if (isTablet && !isMobileMenuOpen) {
        setIsExpanded(false);
      }
    };

    // Check on mount and when isMobileMenuOpen changes
    checkTabletState();

    // Listen for resize events to handle viewport changes
    let resizeTimer: NodeJS.Timeout;
    const handleResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(checkTabletState, 150);
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      clearTimeout(resizeTimer);
    };
  }, [isMobileMenuOpen]);

  // Focus search input when expanded (if many locations)
  useEffect(() => {
    if (isExpanded && hasManyLocations && searchInputRef.current) {
      // Small delay to let animation start
      const t = setTimeout(() => searchInputRef.current?.focus(), 100);
      return () => clearTimeout(t);
    }
  }, [isExpanded, hasManyLocations]);

  /**
   * Update recent locations: push selected to top, keep 3 max
   */
  const updateRecents = useCallback((location: LocationData) => {
    setRecentIds((prev) => {
      const filtered = prev.filter((id) => id !== location.id);
      const updated = [location.id, ...filtered].slice(0, RECENT_COUNT);
      saveRecentIds(updated);
      return updated;
    });
  }, []);

  /**
   * Handle selecting a location
   */
  const handleSelectLocation = useCallback(
    (location: LocationData) => {
      selectLocation(location);
      if (hasManyLocations) {
        updateRecents(location);
      }
      // Clear search after selection
      if (searchQuery) {
        setSearchQuery("");
      }
      // Navigate to Live Cameras on location change
      navigate("/camera");
    },
    [selectLocation, hasManyLocations, updateRecents, searchQuery, navigate],
  );

  /**
   * Handle selecting "My Organization" (null location = org level)
   * Navigate to /camera.
   */
  const handleSelectOrg = useCallback(() => {
    selectLocation(null);
    if (searchQuery) {
      setSearchQuery("");
    }
    navigate("/camera");
  }, [selectLocation, searchQuery, navigate]);

  /**
   * Snap scroll handler
   */
  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    // debounced snap — on scroll end, snap to nearest item
    const timer = setTimeout(() => {
      const offset = el.scrollTop;
      const snapped = Math.round(offset / SNAP_UNIT) * SNAP_UNIT;
      if (Math.abs(offset - snapped) > 2) {
        el.scrollTo({ top: snapped, behavior: "smooth" });
      }
    }, 150);
    return () => clearTimeout(timer);
  }, []);

  // Spring animation config for width
  const springTransition = {
    type: "spring" as const,
    stiffness: 350,
    damping: 32,
    mass: 0.8,
  };

  if (locations.length === 0) {
    return null;
  }

  const isOrgSelected = selectedLocation === null;

  const { t } = useTranslation();

  return (
    <motion.aside
      ref={sidebarRef}
      className={`${styles.sidebar} ${isMobileMenuOpen ? styles.sidebarMobileOpen : ""}`}
      initial={false}
      animate={{ width: isExpanded ? EXPANDED_WIDTH : COLLAPSED_WIDTH }}
      transition={springTransition}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className={styles.content}>
        {/* Organization Button (sticky) */}
        <div className={styles.stickySection}>
          <button
            className={`${styles.locationItem} ${styles.orgButton} ${isOrgSelected ? styles.selected : ""}`}
            onClick={handleSelectOrg}
            title={t('locationSelector.myOrganization')}
          >
            <div className={styles.initialsBox}>
              <OrgIcon />
            </div>
            <AnimatePresence>
              {isExpanded && (
                <motion.span
                  className={styles.locationName}
                  initial={{ opacity: 0, filter: "blur(4px)" }}
                  animate={{ opacity: 1, filter: "blur(0px)" }}
                  exit={{ opacity: 0, filter: "blur(4px)" }}
                  transition={{ duration: 0.2 }}
                >
                  {t('locationSelector.myOrganization')}
                </motion.span>
              )}
            </AnimatePresence>
          </button>

          <div className={styles.divider} />

          {/* Recent Locations (only for >6 locations, sticky) */}
          {hasManyLocations && recentLocations.length > 0 && (
            <>
              {recentLocations.map((loc) => (
                <button
                  key={`recent-${loc.id}`}
                  className={`${styles.locationItem} ${selectedLocation?.id === loc.id ? styles.selected : ""}`}
                  onClick={() => handleSelectLocation(loc)}
                  title={loc.name}
                >
                  {!isExpanded && (
                    <div className={styles.initialsBox}>
                      {getInitials(loc.name)}
                    </div>
                  )}
                  {isExpanded && (
                    <motion.span
                      className={styles.locationNameFull}
                      initial={{ opacity: 0, filter: "blur(4px)" }}
                      animate={{ opacity: 1, filter: "blur(0px)" }}
                      exit={{ opacity: 0, filter: "blur(4px)" }}
                      transition={{ duration: 0.2 }}
                    >
                      {loc.name}
                    </motion.span>
                  )}
                </button>
              ))}
              <div className={styles.divider} />
            </>
          )}

          {/* Search (sticky, only for >6 locations) */}
          {hasManyLocations && (
            <div className={styles.searchContainer}>
              {isExpanded ? (
                <div className={styles.searchInputWrapper}>
                  <SearchIcon />
                  <input
                    ref={searchInputRef}
                    className={styles.searchInput}
                    type="text"
                    placeholder="Search..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  {searchQuery && (
                    <button
                      className={styles.searchClear}
                      onClick={() => setSearchQuery("")}
                      aria-label="Clear search"
                    >
                      ✕
                    </button>
                  )}
                </div>
              ) : (
                <button
                  className={styles.searchCollapsed}
                  title={t('locationSelector.searchLocations')}
                >
                  <SearchIcon />
                </button>
              )}
            </div>
          )}
        </div>

        {/* All Locations (scrollable) */}
        <div
          ref={scrollRef}
          className={styles.scrollableSection}
          onScroll={handleScroll}
        >
          {filteredLocations.map((loc) => (
            <button
              key={loc.id}
              className={`${styles.locationItem} ${selectedLocation?.id === loc.id ? styles.selected : ""}`}
              onClick={() => handleSelectLocation(loc)}
              title={loc.name}
            >
              {!isExpanded && (
                <div className={styles.initialsBox}>
                  {getInitials(loc.name)}
                </div>
              )}
              {isExpanded && (
                <motion.span
                  className={styles.locationNameFull}
                  initial={{ opacity: 0, filter: "blur(4px)" }}
                  animate={{ opacity: 1, filter: "blur(0px)" }}
                  exit={{ opacity: 0, filter: "blur(4px)" }}
                  transition={{ duration: 0.2 }}
                >
                  {loc.name}
                </motion.span>
              )}
            </button>
          ))}
          {filteredLocations.length === 0 && searchQuery && (
            <div className={styles.noResults}>{t('locationSelector.noLocationsFound')}</div>
          )}
        </div>
      </div>

      {/* Fade mask at bottom */}
      <div className={styles.fadeMask} />
    </motion.aside>
  );
};

export default LocationSelector;
